import React, {useEffect, useState} from 'react';
import { rotate } from '@pdfme/manipulator';
import { saveAs } from 'file-saver';
import QuadralContainer from "@quadral/quadral-component-common/dist/components/container";
import QuadralButton from "@quadral/quadral-component-common/dist/components/button";
import QuadralButtonIcon from "@quadral/quadral-component-common/dist/components/buttonIcon";
import QuadralCheckBox from "@quadral/quadral-component-common/dist/components/checkbox"
import QuadralTypography from "@quadral/quadral-component-common/dist/components/typography"
import { onlineProcessesService } from '../../../../services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faEye, faRotateLeft, faRotateRight, faXmark } from '@fortawesome/free-solid-svg-icons';

export default function ReceptionBox({receptionBox, handlePageSelection, allSelected, completedRows, allUnassignedDocs, toggleSelectAll, makePageId , confirmAssignation, docIndex, selectedUnattachedFile, selectedFileByDoc, setSelectedFileByDoc, statuses, getDocStatusCount, isPageSelected,...props}) {
    const [rotationAngles, setRotationAngles] = useState({});
    const [selectedPreview, setSelectedPreview] = useState();
    const [currentPage, setCurrentPage] = useState(1);
    const [blobUrls, setBlobUrls] = useState({});
    const [isTooLarge, setIsTooLarge] = useState(false);
    const PAGE_SIZE = 8;

    const currentDoc = receptionBox?.[docIndex];

    useEffect(()=>{
        setIsTooLarge(currentDoc?.isTooLarge)
    }, [currentDoc])

    useEffect(()=>{
        setCurrentPage(1);
        setSelectedPreview(null);
    },[docIndex])

    function createBlobUrl(base64Data, mimeType) {
        try {
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], {type: mimeType});
            return URL.createObjectURL(blob);
        } catch (error) {
            console.error('Error creating blob URL:', error);
            return null;
        }
    }

    useEffect(() => {
        return () => {
            Object.values(blobUrls).forEach(url => {
                if (url) URL.revokeObjectURL(url);
            });
        };
    }, [blobUrls]);

    useEffect(() => {
        if (!currentDoc?.files) return;
        
        const currentFiles = getFilesByPage(currentDoc?.files, currentPage, PAGE_SIZE);
        const newBlobUrls = {};

        currentFiles.forEach((page, i) => {
            const indexFilePerPage = (currentPage - 1) * PAGE_SIZE + i;
            const pageId = makePageId(docIndex, docIndex, indexFilePerPage);

            if (page.data && !blobUrls[pageId]) {
                newBlobUrls[pageId] = createBlobUrl(page.data, page.mimeType);
            }
        });

        if (Object.keys(newBlobUrls).length > 0) {
            setBlobUrls(prev => ({...prev, ...newBlobUrls}));
        }
    }, [currentDoc, currentPage, blobUrls, docIndex, makePageId]);

    function getFilesByPage(files, currentPage, pageSize) {
        if (!files) return [];
        const start = (currentPage - 1) * pageSize;
        return files.slice(start, start + pageSize);
    }

    function getTotalPages(files, pageSize) {
        if (!files) return 1;
        return Math.max(1, Math.ceil(files.length / pageSize));
    }

    function prev() {
        setCurrentPage(p => Math.max(1, p - 1));
    }

    function next() {
        setCurrentPage(p => Math.min(getTotalPages(currentDoc?.files, PAGE_SIZE), p + 1));
    }

    function preview(i, page) {
        setSelectedPreview(page);
    }

    function rotatePage(index, rotatevalue){
        setRotationAngles(prev => ({
            ...prev,
            [index]: ((prev[index] || 0) + rotatevalue) % 360
        }));
    }

    function getClassName(index, className){
        const isSelected = selectedUnattachedFile?.some((p) => p.index === index);
        if(isSelected) {
            return className === 'file-container' ? `${className} select` : `${className} selected`;
        } else {
            return className;
        }
    }

    async function readyAssignation(){
        await handlePageAssignmentRotation(selectedFileByDoc)
        .then(dataReceived => {
            props.setRotatedFiles(dataReceived);
            return (confirmAssignation({rotatedFiles: dataReceived}));
        })        
    }

    function uint8ToBase64(uint8Array) {
        let binary = "";
        const len = uint8Array.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(uint8Array[i]);
        }
        return btoa(binary);
    }

    async function rotateFile(pageId) {
        if(!rotationAngles[pageId] || rotationAngles[pageId] === 0){
            return;
        }
        const [, , indexFilePerPage] = pageId.split("-");
        const currentBase64 = currentDoc['files'][indexFilePerPage-1]['data'];
        const byteArray = Uint8Array.from(atob(currentBase64), c => c.charCodeAt(0));
        const arrayBuffer = byteArray.buffer;
        const rotatedDocument = await rotate(arrayBuffer, (rotationAngles[pageId]));
        const newBase64 = uint8ToBase64(rotatedDocument);
        return newBase64;
    }

    async function handlePageAssignmentRotation(receivedFile){
        const selectedIds = [];
        for (const key in receivedFile) {
            receivedFile[key].forEach(file => {
                if(rotationAngles[file.index]){
                    selectedIds.push(file.index)
                }
            });
        }

        if(selectedIds === []){
            return;
        }

        const rotatedFiles = await Promise.all(
            Object.entries(receivedFile).map(async ([key, arr]) => {
                const newArr = await Promise.all(
                    arr.map(async file => {
                        if (selectedIds.includes(file.index)) {
                            const newSrc = await rotateFile(file.index);
                            return { ...file, src: 'data:application/pdf;base64,' + newSrc };
                        }else{
                            return file;
                        }
                        
                    })
                );
                return [key, newArr];
            })
        );

        const updatedFiles = Object.fromEntries(rotatedFiles);

        return(updatedFiles);
    }

    async function downloadZIP(){
        props.setLoader(true);
        const docName = currentDoc.originalDocumentName;
        const removePdfSuffix = docName.replace(/\.pdf$/i, "");
        const patches = [];

        patches.push({
            technicalNames: [docName],
            zipFilename: removePdfSuffix
        });

        try {
            const doc = await onlineProcessesService.downloadDocumentToReceptionBox(props.participationId, patches);

            let blob = new Blob([doc], {type: "application/zip"});
            saveAs(blob, `${removePdfSuffix}.zip`);
            props.setLoader(false);
        } catch (error) {
            console.error("Erreur téléchargement :", error);
            props.setLoader(false);
        }
    }

    return (       
        <>
            <div class="detail-container-view" style={{display: 'flex', justifyContent:'space-between'}}>
                <div style={{display: 'flex', gap: '5rem', marginBottom: '1rem'}}>
                    <span>
                        <QuadralButtonIcon
                            size='small'
                            title="Précédent" 
                            disabled={currentPage === 1}
                            onClick={prev}
                            icon={<FontAwesomeIcon icon={faChevronLeft}/>}
                        />
                        <span style={{fontSize: "1.1rem", margin: "0.5rem 1.5rem 0 1.5rem"}}>
                            {currentPage}/{getTotalPages(currentDoc?.files, PAGE_SIZE)}
                        </span>
                        <QuadralButtonIcon
                            size='small'
                            title="Suivant" 
                            disabled={currentPage === getTotalPages(currentDoc?.files, PAGE_SIZE)} 
                            onClick={next}
                            icon={<FontAwesomeIcon icon={faChevronRight}/>}
                        />
                    </span>
                    <span style={{fontSize: "1.1rem", display: "flex"}}>
                        <label className={'selectAllCheckBox'}>
                            <QuadralContainer orientation='row' sx={{gap: '5px'}}>
                                <input
                                    checked={allSelected}
                                    onChange={e => {
                                        toggleSelectAll(e.target.checked);
                                    }}
                                    type="checkbox"
                                    title="Tout sélectionner"
                                    disabled={!allUnassignedDocs?.length || isTooLarge}
                                />
                                <QuadralTypography variant='normal'>Tout sélectionner</QuadralTypography>
                            </QuadralContainer>
                            
                            <span className="checkmark"></span>
                        </label>
                    </span>
                </div>
                <span>
                    <div style={{display: 'flex', justifyContent:'flex-end'}}>
                        <QuadralContainer orientation='right' xl={7} sx={{gap: '10px'}}>
                            <QuadralButton 
                                label="Annuler" 
                                color='error' 
                                onClick={props.cancel}
                            >
                                Annuler
                            </QuadralButton>
                        </QuadralContainer>
                        <QuadralButton 
                            label="Valider l'association" 
                            disabled={!!completedRows[docIndex] || isTooLarge}
                            color='success' 
                            onClick={()=>readyAssignation()}
                        >
                            Valider
                        </QuadralButton>
                    </div>
                </span>
            </div>
            <QuadralContainer data-layout='ModalMainXL' orientation='row'>
                {isTooLarge ? (
                    <>
                    <QuadralContainer orientation='column' className="supporting-doc-list">
                        <div class="d-flex" style={{flexDirection: 'column', gap: '1rem'}}>
                            <div class="panel-title detail-title c-pb-0" style={{margin: '7px 0px'}}>
                                Erreur : fichier trop lourd
                            </div>
                            <p> Fichier trop lourd pour affichage, cliquez ici pour le télécharger : </p>
                            <div class="c-mb-2" style={{
                                display: 'flex', 
                                justifyContent: 'flex-start'
                            }}>
                                <button class="btn btn-icon" onClick={() => downloadZIP()}>
                                    <i class="fa-thin fa-cloud-arrow-down" style={{
                                        fontSize: '1.2rem',
                                        marginRight: '0.5rem',
                                        insetInline: 'auto'
                                    }}/>
                                    Télécharger le document
                                </button>
                            </div>
                        </div>
                    </QuadralContainer>
                    </>
                ) : (
                    <>
                    <QuadralContainer className="unattachedFile-wrapper" xl={(selectedPreview && true) ? '' : 12} style={{ padding: '0' }}>
                        {(() => {
                            const blocks = [];
                            const currentFiles = getFilesByPage(currentDoc?.files, currentPage, PAGE_SIZE);

                            currentFiles.forEach((page, i) => {
                                const indexFilePerPage = (currentPage - 1) * PAGE_SIZE + i;
                                const pageId = makePageId(docIndex, docIndex, indexFilePerPage);

                                blocks.push(
                                    <div className='unattachedFile_item'>
                                        <div key={pageId} className='unattachedFile_card'>
                                            <div className={getClassName(pageId, 'unattachedFile_toolbar')}>
                                                <div className={getClassName(pageId, 'unattachedFile_toolbar_checkbox')}>
                                                    <label className={getClassName(pageId, 'custom-checkbox')}>
                                                        <QuadralCheckBox
                                                            checked={selectedUnattachedFile?.some(p => p.index === pageId)}
                                                            onChange={(e) => handlePageSelection(e.target.checked, {
                                                                index: pageId,
                                                                src: `data:${page.mimeType};base64,${page.data}`,
                                                                alt: `${currentDoc.name} - Page ${indexFilePerPage + 1}`,
                                                                documentName: currentDoc.name,
                                                                documentIndex: docIndex
                                                            })}
                                                            type="checkbox"
                                                            title="Sélectionner"
                                                        />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </div>

                                                <div className={getClassName(pageId, 'unattachedFile_toolbar_buttons')}>
                                                    <QuadralButtonIcon size="small" onClick={() => rotatePage(pageId, -90)} title="Tourner à gauche" icon={<FontAwesomeIcon icon={faRotateLeft}/>}/>
                                                    <QuadralButtonIcon size="small" onClick={() => rotatePage(pageId, 90)} title="Tourner à droite" icon={<FontAwesomeIcon icon={faRotateRight}/>}/>
                                                    <QuadralButtonIcon size="small" onClick={() => preview(pageId, page)} title="Prévisualiser" icon={<FontAwesomeIcon icon={faEye}/>}/>
                                                </div>
                                            </div>

                                            <div className={getClassName(pageId, 'file-container')}>
                                                {page.data && blobUrls[pageId] && (
                                                    <iframe
                                                        className="unattachedFile"
                                                        src={`${blobUrls[pageId]}#toolbar=0&navpanes=0&zoom=page-fit`}
                                                        height="240px"
                                                        title={`PDF - Page ${i + 1}`}
                                                        style={{
                                                            transform: `rotate(${rotationAngles[pageId] || 0}deg)`,
                                                            transition: 'transform 0.3s ease',
                                                            border: 'none',
                                                            overflow: 'hidden',
                                                            maxHeight: '100%',
                                                            maxWidth: '100%',
                                                            boxShadow: '0px 0 8px rgba(0, 0, 0, 0.1)!important',
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <QuadralTypography variant=''>Page {indexFilePerPage+1}</QuadralTypography>
                                    </div>
                                )
                            })
                            return blocks;
                        })()}
                    </QuadralContainer>
                    {(selectedPreview && true) && (
                        <QuadralContainer orientation='column' style={{ minWidth: '33.5%', maxWidth: '50%', padding: '0' }}>
                            <div className="preview_zone">
                                <div className="preview-close" style={{ backgroundColor: 'grey' }}>
                                    <QuadralButtonIcon
                                        size='small'
                                        onClick={() => setSelectedPreview(null)} 
                                        title="Fermer la prévisualisation"
                                        icon={<FontAwesomeIcon icon={faXmark}/>}
                                    />
                                </div>
                                {selectedPreview && (() => {
                                    const pageId = Object.keys(blobUrls).find(key => key.includes(selectedPreview.data?.slice(0, 20))) || makePageId(docIndex, docIndex, selectedPreview.index);
                                    const blobUrl = blobUrls[pageId] || createBlobUrl(selectedPreview.data, selectedPreview.mimeType);

                                    return (
                                        <object
                                            title="preview-pdf"
                                            type="application/pdf"
                                            width="100%"
                                            height="100%"
                                            data={`${blobUrl}#toolbar=0&navpanes=0&view=FitH`}
                                            style={{
                                                minHeight: '839px',
                                                border: 'none',
                                                width: '100%',
                                                height: '100%',
                                            }}
                                        ></object>
                                    );
                                })()}
                            </div>
                        </QuadralContainer>
                    )}
                    </>
                )}
            </QuadralContainer>
        </>
    );
}